import ky from 'ky'
import { QueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import Store from '../utils/localstorage'
import { AUTH_TOKEN, GRANT_TOKEN, REFRESH_TOKEN } from '../utils/constants'

// Setup queryClient
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      staleTime: 60000,
    },
  },
})

type BaseOptions = {
  handledCodes?: number[]
  throwErrorFromKy?: boolean
}

const baseApi = ({ throwErrorFromKy = true }: BaseOptions) =>
  ky.extend({
    timeout: 30000,
    // throw error into console
    throwHttpErrors: throwErrorFromKy,
    retry: 0,
  })

type Options = {
  useRefreshToken?: boolean
  useGrantToken?: boolean
  useGrantTokenAsAuth?: boolean
} & BaseOptions

// Used for fetching data with React Query
export const api = ({
  useGrantToken,
  useRefreshToken,
  useGrantTokenAsAuth,
  ...base
}: Options = {}) =>
  baseApi(base).extend({
    prefixUrl: '/api/v1',
    hooks: {
      beforeRequest: [
        (request) => {
          const authToken =
            Store.get<string>(AUTH_TOKEN) || sessionStorage.getItem(AUTH_TOKEN)

          if (useRefreshToken) {
            const refreshToken =
              Store.get<string>(REFRESH_TOKEN) ||
              sessionStorage.getItem(REFRESH_TOKEN)
            if (refreshToken)
              request.headers.set('Authorization', `Bearer ${refreshToken}`)
          } else if (authToken) {
            request.headers.set('Authorization', `Bearer ${authToken}`)
          }

          if (useGrantToken || useGrantTokenAsAuth) {
            const grantToken = Store.get<string>(GRANT_TOKEN)
            if (useGrantToken && grantToken)
              request.headers.set('grant', grantToken)
            if (useGrantTokenAsAuth && grantToken)
              request.headers.set('Authorization', `Bearer ${grantToken}`)
          }
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          if (!useRefreshToken && !useGrantToken && response.status === 410) {
            const data = await baseApi({}).post('/api/v1/auth/refresh', {
              headers: {
                Authorization: `Bearer ${Store.get(REFRESH_TOKEN)}`,
              },
            })
            if (data) {
              const tokens: {
                refresh: string
                access: string
              } = await data.json()

              if (tokens && tokens.access) {
                Store.set(AUTH_TOKEN, tokens.access)
                Store.set(REFRESH_TOKEN, tokens.refresh)

                request.headers.set(
                  'Authorization',
                  `Bearer ${Store.get(AUTH_TOKEN)}`
                )

                return ky(request)
              }
            }
          }

          if (
            !useRefreshToken &&
            !useGrantToken &&
            response.status === 302 &&
            !!i18next.resolvedLanguage
          ) {
            window.location.href = `/${i18next.resolvedLanguage}/${i18next.t(
              'urls.login'
            )}`
          }

          return response
        },
      ],
    },
  })
